import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { DateRange } from "./dateRangePresets";

import { BottomSheet } from "../../../BottomSheet";
import { Button as ButtonV2 } from "../../../Button";
import { ArrowDownIcon, ChevronRightIcon } from "../../../Icons";
import { MobileDateRangeBottomSheet } from "../../../MobileDateRange";
import { Typo } from "../../../Typo";
import {
  formatDateRangeLabel,
  matchDateRangePreset,
  useDateRangePresets,
} from "./dateRangePresets";

type MobileDateRangeChipProps = {
  value: DateRange;
  open: boolean;
  onOpen: () => void;
  className?: string;
};

/**
 * One-off "Data time range" trigger pill (PART-4052 §4.5/Q3 decision — not
 * built on `LanguageFilterChip`), showing the current range's preset label
 * (or formatted dates). `open`/`onOpen` are controlled by the caller: this
 * renders only the button — the sheets it opens live in
 * `MobileDateRangeChipSheets` below, rendered as a sibling rather than a
 * child so a parent sheet that this chip is nested inside can hide instead
 * of unmounting (see that component's own comment for why that split
 * matters).
 */
export const MobileDateRangeChip = ({
  value,
  open,
  onOpen,
  className,
}: MobileDateRangeChipProps) => {
  const { t, i18n } = useTranslation();
  const presets = useDateRangePresets();

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={t(
        "subIBReport_dateRangeChipLabel",
        "Data time range: {{range}}",
        {
          range: formatDateRangeLabel(value, presets, i18n.language),
        },
      )}
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border border-stroke-primary px-3 py-1.5 hover:bg-surface-tonal-default ${className ?? ""}`}
    >
      <Typo element="span" type="labelXS" textColor="text-text-primary">
        {formatDateRangeLabel(value, presets, i18n.language)}
      </Typo>
      <ArrowDownIcon className="h-3 w-3 text-text-primary" aria-hidden="true" />
    </button>
  );
};

type MobileDateRangeChipSheetsProps = {
  value: DateRange;
  onChange: (range: DateRange) => void;
  open: boolean;
  onClose: () => void;
  /** Distinguishes radio group `name`s when more than one chip is on screen at once. */
  name?: string;
};

/**
 * Owns `MobileDateRangeChip`'s two sheets (preset radios, then a "Custom
 * date range" push into the shared `MobileDateRangeBottomSheet`/
 * `MobileDateRangePicker`). Deliberately a separate component from the
 * trigger button, rendered as that trigger's SIBLING rather than nested
 * inside it — and, in turn, as a sibling of whatever sheet the trigger
 * itself is nested inside (e.g. `MobileProgramDetailSheet`'s own
 * `BottomSheet`), matching the same `isOpen`-gated-sibling-sheets pattern
 * `MobileProgramFilterSheet` uses for its own preset/custom pair.
 *
 * This split fixes a real bug: the two sheets used to be bundled into
 * `MobileDateRangeChip` itself and rendered as a CHILD of
 * `MobileProgramDetailSheet`'s own `BottomSheet`. That outer sheet
 * implemented PART-4052 §4.5's "replace, don't stack" rule by setting its
 * own `isOpen` to `false` whenever this one opened — but `BottomSheet`
 * fully unmounts (`return null`) ~300ms after `isOpen` goes false, which
 * tore down every descendant, including this component and its still-open
 * sheet, abruptly closing it out from under the user a moment after they
 * tapped it open. Keeping these two sheets as true siblings means the outer
 * sheet only has to hide, not unmount, to satisfy "replace, don't stack".
 */
export const MobileDateRangeChipSheets = ({
  value,
  onChange,
  open,
  onClose,
  name = "sm-date-range-chip",
}: MobileDateRangeChipSheetsProps) => {
  const { t, i18n } = useTranslation();
  const presets = useDateRangePresets();
  const [step, setStep] = useState<"presets" | "custom">("presets");
  const [draft, setDraft] = useState<DateRange>(value);

  useEffect(() => {
    if (open) {
      setDraft(value);
      setStep("presets");
    }
  }, [open, value]);

  const selectedPreset = matchDateRangePreset(draft, presets);

  const handleApply = () => {
    onChange(draft);
    onClose();
  };

  const handleCustomApply = (range: { from: Date; to: Date }) => {
    setDraft(range);
    onChange(range);
    onClose();
  };

  return (
    <>
      <BottomSheet
        isOpen={open && step === "presets"}
        onClose={onClose}
        title={t("subIBReport_dataTimeRange", "Data time range")}
        isDraggable
        footer={
          <div className="flex gap-3">
            <ButtonV2
              variant="outline"
              size="md"
              className="flex-1"
              onClick={onClose}
            >
              {t("subIBReport_cancel", "Cancel")}
            </ButtonV2>
            <ButtonV2
              variant="brand"
              size="md"
              className="flex-1"
              onClick={handleApply}
            >
              {t("subIBReport_apply", "Apply")}
            </ButtonV2>
          </div>
        }
      >
        <div className="flex flex-col gap-1">
          {presets.map((preset) => (
            <label
              key={preset.id}
              className="flex cursor-pointer items-center gap-3 py-2"
            >
              <input
                type="radio"
                name={name}
                checked={selectedPreset?.id === preset.id}
                onChange={() => setDraft(preset.range)}
                className="h-5 w-5"
              />
              <Typo element="span" type="bodySM" textColor="text-text-primary">
                {preset.label}
              </Typo>
            </label>
          ))}
          <button
            type="button"
            className="flex items-center gap-3 py-2 text-left"
            onClick={() => setStep("custom")}
          >
            <input
              type="radio"
              name={name}
              readOnly
              checked={!selectedPreset}
              className="h-5 w-5"
            />
            <span className="flex flex-1 flex-col">
              <Typo element="span" type="bodySM" textColor="text-text-primary">
                {t("subIBReport_customDateRange", "Custom date range")}
              </Typo>
              {!selectedPreset && (
                <Typo
                  element="span"
                  type="bodyXS"
                  textColor="text-text-secondary"
                >
                  {formatDateRangeLabel(draft, presets, i18n.language)}
                </Typo>
              )}
            </span>
            <ChevronRightIcon
              className="h-4 w-4 text-icon-primary"
              aria-hidden="true"
            />
          </button>
        </div>
      </BottomSheet>

      <MobileDateRangeBottomSheet
        isOpen={open && step === "custom"}
        onClose={onClose}
        onBack={() => setStep("presets")}
        onApply={handleCustomApply}
        // "All time" isn't a bounded range — seeding the custom picker with
        // its 2021-to-today span would misrepresent it as one. Open blank
        // instead when that's the currently selected preset (PART-4130).
        initialRange={selectedPreset?.id === "allTime" ? null : draft}
        defaultRange={presets.find((p) => p.id === "last30days")?.range ?? null}
        isDraggable
      />
    </>
  );
};
